import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";

const KEY = "mibinky-cookie-consent";
export const COOKIE_SETTINGS_EVENT = "mibinky:cookie-settings";

const applyChoice = (accepted) => {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: accepted ? "granted" : "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    if (accepted) {
      window.gtag("config", "G-QJ6N9LEP19");
    }
  }
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let stored = null;
    try {
      stored = window.localStorage.getItem(KEY);
    } catch (e) {}
    if (!stored) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, open);
  }, []);

  const choose = (accepted) => {
    try {
      window.localStorage.setItem(KEY, accepted ? "accepted" : "rejected");
    } catch (e) {}
    applyChoice(accepted);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 28 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4 sm:px-6 sm:pb-6"
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="mx-auto flex max-w-3xl flex-col gap-4 rounded-3xl border border-slate-100 bg-white/95 p-5 shadow-2xl shadow-binky-blue/25 backdrop-blur-xl sm:flex-row sm:items-center sm:gap-6 sm:p-6">
            <div className="flex items-start gap-3.5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-binky-pinklight text-binky-pink">
                <Cookie className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-base font-bold text-slate-900">
                  A quick word about cookies
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-slate-600 sm:text-sm">
                  We&apos;d like to use Google Analytics cookies to understand how parents use our
                  website — only with your permission. No advertising cookies are used, and nothing
                  is stored before you choose.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-col gap-2.5 sm:flex-row sm:items-center">
              <button
                data-testid="cookie-accept-button"
                onClick={() => choose(true)}
                className="rounded-full bg-binky-pink px-6 py-3 text-sm font-bold text-white shadow-lg shadow-binky-pink/30 transition-transform duration-300 hover:scale-[1.03] active:scale-95"
              >
                Accept Analytics
              </button>
              <button
                data-testid="cookie-reject-button"
                onClick={() => choose(false)}
                className="rounded-full border-2 border-binky-blue/20 bg-white px-6 py-3 text-sm font-bold text-binky-blue transition-all duration-300 hover:border-binky-blue active:scale-95"
              >
                Reject Non-Essential Cookies
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
